import { useEffect, useRef } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { FormProvider, useForm } from 'react-hook-form';
import _ from 'lodash';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import { AUTO_COMPLETE_DEFAULT, MANUAL_ERROR_TYPE, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import NgpTextField from '../../form/NgpTextField';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import { WAN_INTERFACE_RULE_FIELD } from './WanInterfaceAdminConstants';

const formId = 'frmWanRule';
const uniqueErrorText = i18n._(t`Must be unique`);
const addButtonText = i18n._(t`Add`);
const updateButtonText = i18n._(t`Update`);
const defaultFormValues = {};

export default function WanRuleForm({
    selectedList = [],
    selectedRecord = {},
    setSelectedRecord,
    open,
    setOpen,
    onConfirm,
}) {
    const methods = useForm({ ...USE_FORM_DEFAULT_OPTIONS, defaultValues: {} });
    const internalId = useRef(null);
    const buttonText = useRef(addButtonText);
    const { handleSubmit, formState, reset, setError } = methods;

    useEffect(() => {
        if (!_.isEmpty(selectedRecord)) {
            internalId.current = selectedRecord[INTERNAL_ID_FIELD];
            buttonText.current = selectedRecord[INTERNAL_ID_FIELD] ? updateButtonText : addButtonText;
            reset(selectedRecord);
        }
    }, [selectedRecord, reset]);

    const handleCleanup = () => {
        internalId.current = null;
        buttonText.current = addButtonText;
        setSelectedRecord(undefined);
        reset(defaultFormValues);
    };

    const handleFormSubmit = (dataToSave = {}) => {
        if (internalId) {
            _.set(dataToSave, INTERNAL_ID_FIELD, internalId.current);
        }

        if (
            _.filter(selectedList, (listItem) => {
                return (
                    _.toLower(listItem[WAN_INTERFACE_RULE_FIELD]) === _.toLower(dataToSave[WAN_INTERFACE_RULE_FIELD]) &&
                    listItem[INTERNAL_ID_FIELD] !== dataToSave[INTERNAL_ID_FIELD]
                );
            }).length > 0
        ) {
            setError(WAN_INTERFACE_RULE_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: uniqueErrorText,
            });
            return;
        }

        return Promise.resolve()
            .then(() => onConfirm(dataToSave))
            .then(handleCleanup);
    };

    return (
        <NgpTwoButtonDialog
            title={<Trans>Wan Interface Rule</Trans>}
            confirmButtonText={buttonText.current}
            cancelButtonText={<Trans>Cancel</Trans>}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            confirmButtonDisabled={!formState.isValid}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleFormSubmit)}
        >
            <FormProvider {...methods}>
                <NgpForm
                    formId={formId}
                    initialized
                    loading={false}
                    showSave={false}
                    formPadding={false}
                    showRequiredFieldsTip={false}
                    submitFunction={handleFormSubmit}
                >
                    <FormSection>
                        <NgpTextField
                            fieldName={WAN_INTERFACE_RULE_FIELD}
                            label={i18n._(t`Rule`)}
                            required
                            inputProps={{
                                size: 50,
                                maxLength: 50,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                        />
                    </FormSection>
                </NgpForm>
            </FormProvider>
        </NgpTwoButtonDialog>
    );
}
